import React, { useRef, useEffect } from 'react';
import styles from './FeatureSection.module.css';
import { GlassButton } from '../CtaButton/GlassButton';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

interface FeatureCardProps {
  title: string;
  description: string;
  imageSrc: string;
  buttonText: string;
  buttonLink: string;
  buttonType?: 'primary' | 'secondary';
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  imageSrc,
  buttonText,
  buttonLink,
  buttonType = 'primary'
}) => {
  return (
    <div className={styles.featureCard}>
      <div className={styles.imageContainer}>
        <img src={imageSrc} alt={title} className={styles.featureImage} />
      </div>
      <div className={styles.contentContainer}>
        <h3 className={styles.featureTitle}>{title}</h3>
        <p className={styles.featureDescription}>{description}</p>
        <div className={styles.buttonContainer}>
          <GlassButton href={buttonLink} buttonType={buttonType}>{buttonText}</GlassButton>
        </div>
      </div>
    </div>
  );
};

const FeatureSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const card1Ref = useRef<HTMLDivElement>(null);
  const card2Ref = useRef<HTMLDivElement>(null);
  const card3Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Create animation for feature cards
    const cardRefs = [card1Ref.current, card2Ref.current, card3Ref.current].filter(Boolean);
    
    if (sectionRef.current && cardRefs.length > 0) {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      });

      // Animate cards sequentially
      cardRefs.forEach((card, index) => {
        if (card) {
          timeline.fromTo(
            card,
            { 
              opacity: 0,
              y: 50
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              delay: index * 0.2
            },
            index === 0 ? "+=0" : "-=0.6"
          );
        }
      });
    }
  }, []);

  return (
    <div className={styles.featureSectionContainer} ref={sectionRef}>
      <div className={styles.featureCardsContainer}>
        <div ref={card1Ref} className={styles.cardWrapper}>
          <FeatureCard
            title="Listen with Friends"
            description="Browse, search, and discover music in a full-featured web player. Create playlists, explore trends, and sync up with friends — all from your browser."
            imageSrc="/assets/images/music-discovery.png"
            buttonText="Join Rythm"
            buttonLink="https://app.rythm.fm/"
          />
        </div>
        <div ref={card2Ref} className={styles.cardWrapper}>
          <FeatureCard
            title="Discord Bot"
            description="The original music bot is back. Play songs directly from chat and instantly listen with friends in your voice channel. No need to leave Discord — just type and play."
            imageSrc="/assets/images/discord-bot.png"
            buttonText="Add bot to your server"
            buttonLink="https://discord.com/oauth2/authorize?client_id=235088799074484224&permissions=8&integration_type=0&scope=bot"
            buttonType="secondary"
          />
        </div>
        <div ref={card3Ref} className={styles.cardWrapper}>
          <FeatureCard
            title="Discord Activity"
            description="Launch Rythm as an Activity to discover and share music in real time on Discord. Perfect for voice chats — no setup required, just press play and listen together."
            imageSrc="/assets/images/discord-activity.png"
            buttonText="Learn more on Discord"
            buttonLink="https://discord.gg/ZtH6rZpa8y"
          />
        </div>
      </div>
    </div>
  );
};

export default FeatureSection; 